import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Leaf, MapPin, Banknote, Briefcase, Search, ArrowRight } from "lucide-react";
import { fmtDate } from "@/lib/cc";
import Seo, { publicUrl } from "@/components/Seo";

export default function PortalJobs() {
  const [jobs, setJobs] = useState([]);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");

  useEffect(() => {
    api.get("/portal/jobs").then(({ data }) => setJobs(data));
  }, []);

  const filtered = jobs.filter((j) => {
    if (!q) return true;
    const s = `${j.title} ${j.location} ${j.department}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  const cityList = Array.from(new Set(jobs.map((j) => j.location).filter(Boolean))).slice(0, 3).join(", ");
  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Rabah Concept Recruitment",
    "description": "CQC-regulated domiciliary care and supported-living recruiter — Care Support Workers, Senior Carers, Registered Managers across the UK.",
    "url": publicUrl("/jobs"),
    "logo": publicUrl("/rabah-logo.png"),
    "telephone": "+44 03303211535",
    "email": "recruitment@rabahconcept.co.uk",
    "address": { "@type": "PostalAddress", "addressCountry": "GB" },
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--cc-bg)" }}>
      <Seo
        title={`Care Jobs${cityList ? ` in ${cityList}` : ""} | Rabah Concept Recruitment`}
        description={`Browse ${jobs.length} live care vacancies at Rabah Concept Recruitment. Care Support Workers, Senior Carers and Registered Manager roles. Apply online today.`}
        canonical={publicUrl("/jobs")}
        jsonLd={localBusiness}
      />
      <header className="cc-topbar px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--cc-primary)" }}>
            <Leaf className="w-4 h-4 text-white" />
          </div>
          <div className="font-display font-bold text-lg">CareConnect</div>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/track" className="btn-ghost">Track application</Link>
          <Link to="/login" className="btn-secondary">Staff sign in</Link>
        </div>
      </header>

        <section className="px-8 py-12 max-w-7xl mx-auto">
          <div className="label-eyebrow mb-3">Live roles</div>
          <h1 className="font-display text-5xl font-bold mb-6">Rabah Concept Recruitment — Current Care Job Vacancies</h1>

        <div className="relative max-w-2xl mb-10">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[var(--cc-text-soft)]" />
          <input className="cc-input pl-11 py-3" placeholder="Search by title, location or department…"
                 value={q} onChange={(e) => { setQ(e.target.value); setParams({ q: e.target.value }); }} data-testid="jobs-search-input" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger">
          {filtered.map((j) => (
            <Link to={`/jobs/${j.slug || j.id}`} key={j.id} className="cc-card cc-card-hover p-7 block" data-testid={`portal-job-${j.id}`}>
              <div className="label-eyebrow mb-2">{j.department}</div>
              <h2 className="font-display text-2xl font-bold mb-3">{j.title}</h2>
              <div className="flex flex-wrap gap-3 text-xs text-[var(--cc-text-soft)] mb-4">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{j.location}</span>
                <span className="flex items-center gap-1"><Banknote className="w-3 h-3" />{j.salary}</span>
                <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{j.employment_type}</span>
              </div>
              <p className="text-sm text-[var(--cc-text-soft)] line-clamp-2 mb-4">{j.description}</p>
              <div className="flex justify-between items-center">
                <span className="text-xs text-[var(--cc-text-soft)]">Posted {fmtDate(j.created_at)}</span>
                <span className="text-sm font-semibold text-[var(--cc-primary)] flex items-center gap-1">View & apply <ArrowRight className="w-3.5 h-3.5" /></span>
              </div>
            </Link>
          ))}
          {filtered.length === 0 && <div className="col-span-2 text-center py-12 text-[var(--cc-text-soft)]">No roles match your search.</div>}
        </div>
      </section>
    </div>
  );
}
